import * as THREE from 'three'
import { getDiceTheme } from './diceThemes.js'

const SIZE = 512

// The d20 UV mapping projects each face onto this triangle, apex pointing up.
const TRI = {
  left: { x: 0.1 * SIZE, y: (1 - 0.2) * SIZE },
  right: { x: 0.9 * SIZE, y: (1 - 0.2) * SIZE },
  apex: { x: 0.5 * SIZE, y: (1 - 0.85) * SIZE }
}

const hashSeed = (n) => {
  let h = 2166136261 ^ n
  h = Math.imul(h ^ (h >>> 15), 2246822507)
  h = Math.imul(h ^ (h >>> 13), 3266489909)
  return (h ^= h >>> 16) >>> 0
}

const rngFor = (n) => {
  let a = hashSeed(n)
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const makeCanvas = () => {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = SIZE
  return canvas
}

const traceTriangle = (ctx, inset = 0) => {
  const cx = (TRI.left.x + TRI.right.x + TRI.apex.x) / 3
  const cy = (TRI.left.y + TRI.right.y + TRI.apex.y) / 3
  const pull = (p) => ({
    x: p.x + (cx - p.x) * inset,
    y: p.y + (cy - p.y) * inset
  })
  const a = pull(TRI.left)
  const b = pull(TRI.right)
  const c = pull(TRI.apex)

  ctx.beginPath()
  ctx.moveTo(a.x, a.y)
  ctx.lineTo(b.x, b.y)
  ctx.lineTo(c.x, c.y)
  ctx.closePath()
}

const paintStoneBase = (ctx, rand, palette) => {
  const gradient = ctx.createRadialGradient(SIZE / 2, SIZE * 0.55, 0, SIZE / 2, SIZE * 0.55, SIZE * 0.7)
  gradient.addColorStop(0, palette.stoneCore)
  gradient.addColorStop(1, palette.stoneEdge)
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, SIZE, SIZE)

  // Mineral grain: short strokes at varying angles read as polished stone under raking light.
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < 260; i++) {
    const x = rand() * SIZE
    const y = rand() * SIZE
    const len = 8 + rand() * 40
    const angle = rand() * Math.PI
    ctx.strokeStyle = `rgba(${palette.grain}, ${0.012 + rand() * 0.03})`
    ctx.lineWidth = 0.5 + rand() * 1.4
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len)
    ctx.stroke()
  }
  ctx.restore()

  ctx.save()
  ctx.globalCompositeOperation = 'multiply'
  for (let i = 0; i < 90; i++) {
    const x = rand() * SIZE
    const y = rand() * SIZE
    const r = 6 + rand() * 34
    ctx.fillStyle = `rgba(0, 0, 0, ${0.06 + rand() * 0.12})`
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}

/**
 * Draws one crop of a rendered material plate, rotated and offset per face,
 * then knocks it back so an emissive numeral still reads against it.
 */
const paintSurface = (ctx, rand, image) => {
  const zoom = 0.55 + rand() * 0.25
  const w = image.width * zoom
  const h = image.height * zoom
  const sx = rand() * (image.width - w)
  const sy = rand() * (image.height - h)

  ctx.save()
  ctx.translate(SIZE / 2, SIZE / 2)
  ctx.rotate(Math.floor(rand() * 4) * (Math.PI / 2))
  ctx.drawImage(image, sx, sy, w, h, -SIZE / 2, -SIZE / 2, SIZE, SIZE)
  ctx.restore()

  const shade = ctx.createRadialGradient(SIZE / 2, SIZE * 0.55, 0, SIZE / 2, SIZE * 0.55, SIZE * 0.72)
  shade.addColorStop(0, 'rgba(0, 0, 0, 0.05)')
  shade.addColorStop(1, 'rgba(0, 0, 0, 0.45)')
  ctx.fillStyle = shade
  ctx.fillRect(0, 0, SIZE, SIZE)
}

const paintVeins = (ctx, rand, glowCtx, palette) => {
  const veins = 2 + Math.floor(rand() * 2)
  for (let v = 0; v < veins; v++) {
    let x = rand() * SIZE
    let y = rand() * SIZE
    const steps = 5 + Math.floor(rand() * 4)
    const path = [{ x, y }]
    for (let s = 0; s < steps; s++) {
      x += (rand() - 0.5) * 150
      y += (rand() - 0.5) * 150
      path.push({ x, y })
    }

    const stroke = (target, width, color) => {
      target.save()
      target.strokeStyle = color
      target.lineWidth = width
      target.lineCap = 'round'
      target.lineJoin = 'round'
      target.beginPath()
      target.moveTo(path[0].x, path[0].y)
      path.slice(1).forEach((p) => target.lineTo(p.x, p.y))
      target.stroke()
      target.restore()
    }

    stroke(ctx, 2.4, 'rgba(0, 0, 0, 0.55)')
    stroke(ctx, 1.1, `rgba(${palette.vein}, ${0.16 + rand() * 0.14})`)
    stroke(glowCtx, 1.4, `rgba(${palette.vein}, ${0.10 + rand() * 0.10})`)
  }
}

const paintEtchedRim = (ctx, glowCtx, accent, palette) => {
  ctx.save()
  traceTriangle(ctx, 0.08)
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)'
  ctx.lineWidth = 9
  ctx.stroke()
  ctx.strokeStyle = accent
  ctx.globalAlpha = 0.5
  ctx.lineWidth = 2.4
  ctx.stroke()
  ctx.restore()

  ctx.save()
  traceTriangle(ctx, 0.16)
  ctx.strokeStyle = `rgba(${palette.vein}, 0.13)`
  ctx.lineWidth = 1.2
  ctx.stroke()
  ctx.restore()

  glowCtx.save()
  traceTriangle(glowCtx, 0.08)
  glowCtx.strokeStyle = accent
  glowCtx.globalAlpha = 0.45
  glowCtx.lineWidth = 2.4
  glowCtx.stroke()
  glowCtx.restore()
}

const numeralFor = (num) => (num === 6 || num === 9 ? `${num}.` : `${num}`)

const paintNumeral = (ctx, glowCtx, num, accent, palette) => {
  const label = numeralFor(num)
  const x = SIZE / 2
  const y = SIZE * 0.6

  const setFont = (target) => {
    target.font = "600 168px 'Cinzel', 'Copperplate Gothic', 'Trajan Pro', Georgia, serif"
    target.textAlign = 'center'
    target.textBaseline = 'middle'
  }

  // Engraved plate: dark recess first, lit edge second — reads as cut into the stone.
  ctx.save()
  setFont(ctx)
  ctx.fillStyle = 'rgba(0, 0, 0, 0.85)'
  ctx.fillText(label, x + 3, y + 4)
  ctx.restore()

  ctx.save()
  setFont(ctx)
  ctx.shadowColor = accent
  ctx.shadowBlur = 26
  ctx.fillStyle = accent
  ctx.fillText(label, x, y)
  ctx.lineWidth = 2
  ctx.strokeStyle = palette.numeralEdge
  ctx.strokeText(label, x, y)
  ctx.restore()

  glowCtx.save()
  setFont(glowCtx)
  glowCtx.shadowColor = accent
  glowCtx.shadowBlur = 34
  glowCtx.fillStyle = accent
  glowCtx.fillText(label, x, y)
  glowCtx.restore()
}

const toTexture = (canvas, colorSpace) => {
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = colorSpace
  texture.anisotropy = 4
  texture.needsUpdate = true
  return texture
}

const accentFor = (num, highlight, palette) => {
  if (!highlight) return palette.accent
  if (num === 1) return palette.critFail
  if (num === 20) return palette.critSuccess
  return palette.critNeutral
}

/**
 * Builds the colour + emissive pair for one d20 face: etched stone with an
 * engraved numeral that stays legible in a near-black scene. The theme decides
 * what that stone is and what burns in the etching.
 */
export const createFaceTexture = (num, { highlight = false, userImage = null, surfaceImage = null, theme = null } = {}) => {
  const palette = (typeof theme === 'string' ? getDiceTheme(theme) : theme)?.face || getDiceTheme().face
  const base = makeCanvas()
  const glow = makeCanvas()
  const ctx = base.getContext('2d')
  const glowCtx = glow.getContext('2d')
  const rand = rngFor(num)
  const accent = accentFor(num, highlight, palette)

  glowCtx.fillStyle = '#000000'
  glowCtx.fillRect(0, 0, SIZE, SIZE)

  if (userImage) {
    ctx.drawImage(userImage, 0, 0, SIZE, SIZE)
    ctx.fillStyle = 'rgba(4, 8, 12, 0.55)'
    ctx.fillRect(0, 0, SIZE, SIZE)
  } else if (surfaceImage) {
    // A rendered material plate. Each face takes a different crop of it, so no
    // two faces of the die are the same piece of metal.
    paintSurface(ctx, rand, surfaceImage)
  } else {
    paintStoneBase(ctx, rand, palette)
    paintVeins(ctx, rand, glowCtx, palette)
  }

  paintEtchedRim(ctx, glowCtx, accent, palette)
  paintNumeral(ctx, glowCtx, num, accent, palette)

  return {
    map: toTexture(base, THREE.SRGBColorSpace),
    emissiveMap: toTexture(glow, THREE.SRGBColorSpace)
  }
}

export default createFaceTexture
